/**
 * 🟥 CUADRO NEGRO V2 — ProjectTreeViewer con edición inline + descarga ZIP.
 *
 * Renderiza un proyecto multi-archivo (project_files = [{path, content}])
 * como árbol de carpetas + visor/editor del archivo seleccionado.
 *
 * V2 cambios sobre V1:
 *  - Modo edición inline (textarea + Guardar/Cancelar) → llama PUT
 *    /api/workspace/projects/{moduleId}/files con un solo archivo.
 *  - Botón "Descargar ZIP" → GET /api/workspace/projects/{moduleId}/download.
 *  - onFilesUpdate callback opcional para que el panel padre refresque
 *    la lista local de project_files sin perder selección.
 *
 * V2 NO hace:
 *  - Crear ni borrar archivos (solo overwrite).
 *  - Renombrar paths.
 *  - Versionado.
 */
import { useMemo, useState, useEffect } from 'react';
import {
  ChevronRight, ChevronDown, File, Folder, FolderOpen,
  Copy, Check, Pencil, Save, X, Loader2, Download,
  Github, Upload, RefreshCw, GitMerge, Zap, History, GitPullRequest,
  Workflow, Rocket, KeyRound,
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';
import ConflictResolutionModal from './ConflictResolutionModal';
import CommitHistoryPanel from './CommitHistoryPanel';
import BranchPicker from './BranchPicker';
import PullRequestsPanel from './PullRequestsPanel';
import CiStatusBadge from './CiStatusBadge';
import WorkflowsPanel from './WorkflowsPanel';
import DeploysPanel from './DeploysPanel';
import EnvVarsPanel from './EnvVarsPanel';

const API = `${BACKEND_URL}/api`;

/** Construye un árbol de nodos desde el array plano {path, content}. */
function buildTree(files) {
  const root = { name: '', path: '', children: {}, isFolder: true };
  for (const f of files || []) {
    if (!f?.path) continue;
    const parts = f.path.split('/').filter(Boolean);
    let cursor = root;
    parts.forEach((part, idx) => {
      const isLast = idx === parts.length - 1;
      if (!cursor.children[part]) {
        cursor.children[part] = {
          name: part,
          path: parts.slice(0, idx + 1).join('/'),
          children: {},
          isFolder: !isLast,
          content: isLast ? f.content : undefined,
        };
      }
      cursor = cursor.children[part];
    });
  }
  return root;
}

const sortNodes = (nodes) => {
  const arr = Object.values(nodes);
  arr.sort((a, b) => {
    if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return arr;
};

const FILE_LANG_HINT = (path) => {
  const ext = (path.split('.').pop() || '').toLowerCase();
  return {
    js: 'javascript', jsx: 'jsx', ts: 'typescript', tsx: 'tsx',
    py: 'python', rb: 'ruby', go: 'go', rs: 'rust',
    json: 'json', md: 'markdown', html: 'html', css: 'css', scss: 'scss',
    yml: 'yaml', yaml: 'yaml', xml: 'xml', sh: 'bash', toml: 'toml',
  }[ext] || 'text';
};

const TreeNode = ({ node, depth = 0, expandedPaths, onToggle, onSelect, selectedPath }) => {
  const children = node.isFolder ? sortNodes(node.children) : [];
  const isExpanded = expandedPaths.has(node.path);
  const isSelected = selectedPath === node.path;

  if (!node.isFolder) {
    return (
      <button
        type="button"
        onClick={() => onSelect(node)}
        className={`w-full flex items-center gap-1.5 px-2 py-1 text-xs rounded text-left hover:bg-slate-700/60 transition-colors ${
          isSelected ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-300'
        }`}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        data-testid={`project-file-${node.path}`}
      >
        <File className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{node.name}</span>
      </button>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(node.path)}
        className="w-full flex items-center gap-1.5 px-2 py-1 text-xs rounded text-left text-slate-200 hover:bg-slate-700/60 transition-colors"
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
      >
        {isExpanded ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
        {isExpanded ? <FolderOpen className="w-3.5 h-3.5 shrink-0 text-emerald-400" /> : <Folder className="w-3.5 h-3.5 shrink-0 text-emerald-400" />}
        <span className="truncate font-medium">{node.name || '/'}</span>
      </button>
      {isExpanded && children.length > 0 && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              onSelect={onSelect}
              selectedPath={selectedPath}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const ProjectTreeViewer = ({
  projectFiles,
  projectName,
  moduleId,
  githubSource,
  autoSyncGithub = false,
  onFilesUpdate,
}) => {
  const { language } = useLanguage();
  const isEs = (language || 'es').toLowerCase().startsWith('es');

  const root = useMemo(() => buildTree(projectFiles), [projectFiles]);
  const topLevel = useMemo(() => sortNodes(root.children), [root]);

  const [expandedPaths, setExpandedPaths] = useState(() => {
    const set = new Set();
    topLevel.forEach((n) => {
      if (n.isFolder) set.add(n.path);
    });
    return set;
  });

  const allFiles = projectFiles || [];
  const [selectedPath, setSelectedPath] = useState(allFiles[0]?.path || null);
  const [copied, setCopied] = useState(false);

  // Estado de edición.
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // 🟥 CUADRO NEGRO V4 — Estado GitHub Push/Pull.
  const [isPushing, setIsPushing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  // 🟥 V4.1 — Estado de conflictos y auto-sync.
  const [conflicts, setConflicts] = useState(null); // null | array
  const [autoSync, setAutoSync] = useState(!!autoSyncGithub);
  const [isTogglingAutoSync, setIsTogglingAutoSync] = useState(false);
  // 🟥 V5 — Historial de commits.
  const [showHistory, setShowHistory] = useState(false);
  // 🟥 V6 — Pull Requests panel.
  const [showPRs, setShowPRs] = useState(false);
  // 🟥 V7 — CI/CD + Deploys + Env Vars panels.
  const [showWorkflows, setShowWorkflows] = useState(false);
  const [showDeploys, setShowDeploys] = useState(false);
  const [showEnvVars, setShowEnvVars] = useState(false);

  useEffect(() => {
    // Si cambian los archivos (otro módulo), seleccionar primer archivo y
    // salir de modo edición. Disable rule: es exactamente lo que queremos.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedPath(allFiles[0]?.path || null);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsEditing(false);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft('');
  }, [projectFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const onToggle = (p) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const selectedFile = allFiles.find((f) => f.path === selectedPath);

  const handleCopy = async () => {
    if (!selectedFile?.content) return;
    try {
      await navigator.clipboard.writeText(selectedFile.content);
      setCopied(true);
      toast.success(isEs ? 'Contenido copiado' : 'Content copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(isEs ? 'No se pudo copiar' : 'Could not copy');
    }
  };

  const handleStartEdit = () => {
    if (!selectedFile) return;
    setDraft(selectedFile.content || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDraft('');
  };

  const handleSaveEdit = async () => {
    if (!selectedFile || !moduleId) return;
    setIsSaving(true);
    try {
      const res = await axios.put(
        `${API}/workspace/projects/${moduleId}/files`,
        {
          files: [{ path: selectedFile.path, content: draft }],
        },
      );
      if (res.data?.module_id) {
        // Actualización local + notificar al padre.
        const newFiles = allFiles.map((f) =>
          f.path === selectedFile.path ? { ...f, content: draft } : f
        );
        onFilesUpdate?.(newFiles);
        setIsEditing(false);
        toast.success(isEs ? 'Cambios guardados' : 'Changes saved');
        // 🟥 V4.1 — Auto-push si autoSync está ON y hay github_source.
        if (autoSync && githubSource) {
          try {
            const pushRes = await axios.post(`${API}/github/push`, {
              module_id: moduleId,
              target_repo: `${githubSource.owner}/${githubSource.repo}`,
              branch: githubSource.branch || 'main',
              commit_message: isEs ? `Auto-sync: ${selectedFile.path}` : `Auto-sync: ${selectedFile.path}`,
              create_if_missing: false,
              private: true,
            });
            if (pushRes.data?.success) {
              const sha = (pushRes.data.commit_sha || '').slice(0, 7);
              toast.success(isEs ? `Auto-push OK · ${sha}` : `Auto-push OK · ${sha}`);
            }
          } catch (e) {
            const detail = e?.response?.data?.detail || e?.message;
            toast.warning(isEs ? `Auto-push falló: ${detail}` : `Auto-push failed: ${detail}`);
          }
        }
      } else {
        toast.error(isEs ? 'Respuesta inesperada del servidor' : 'Unexpected server response');
      }
    } catch (error) {
      const status = error?.response?.status;
      const detailRaw = error?.response?.data?.detail;
      const isPremiumLock = status === 403 && (
        (typeof detailRaw === 'object' && detailRaw?.code === 'PREMIUM_REQUIRED') ||
        (typeof detailRaw === 'string' && /premium/i.test(detailRaw))
      );
      if (isPremiumLock) {
        toast.error(isEs ? 'Editar archivos es Premium' : 'Editing files is Premium-only');
      } else {
        const detail = typeof detailRaw === 'object' ? (detailRaw?.message || 'unknown') : (detailRaw || error?.message || 'unknown');
        toast.error(
          isEs ? `No se pudo guardar: ${detail}` : `Could not save: ${detail}`
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!moduleId) return;
    setIsDownloading(true);
    try {
      const res = await axios.get(
        `${API}/workspace/projects/${moduleId}/download`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Intentar usar filename del header; si no, fallback al nombre del proyecto.
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      link.download = match?.[1] || `${(projectName || 'project').replace(/[^A-Za-z0-9._-]+/g, '-')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(isEs ? 'Descarga iniciada' : 'Download started');
    } catch (error) {
      const status = error?.response?.status;
      const detailRaw = error?.response?.data?.detail;
      const isPremiumLock = status === 403 && (
        (typeof detailRaw === 'object' && detailRaw?.code === 'PREMIUM_REQUIRED') ||
        (typeof detailRaw === 'string' && /premium/i.test(detailRaw))
      );
      if (isPremiumLock) {
        toast.error(isEs ? 'Descargar es Premium' : 'Download is Premium-only');
      } else {
        toast.error(isEs ? 'No se pudo descargar el proyecto' : 'Could not download project');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // 🟥 CUADRO NEGRO V4 — GitHub Pull: refresca el módulo desde el repo.
  const handleGithubPull = async () => {
    if (!moduleId || !githubSource) return;
    setIsPulling(true);
    try {
      // 🟥 V4.1 — Usamos el endpoint con conflictos. Si el módulo no tiene
      // snapshot aún (primer pull tras V4), el backend cae a overwrite y
      // crea el snapshot — futuro pull tendrá 3-way merge real.
      const res = await axios.post(`${API}/github/pull-with-conflicts`, { module_id: moduleId });
      const status = res.data?.status;
      const summary = res.data?.summary || { added: 0, modified: 0, removed: 0, conflicted: 0 };
      if (status === 'conflicts' && Array.isArray(res.data?.conflicts) && res.data.conflicts.length > 0) {
        setConflicts(res.data.conflicts);
        toast.warning(isEs ? `Se detectaron ${res.data.conflicts.length} conflictos` : `${res.data.conflicts.length} conflicts detected`);
      } else if (status === 'no_changes') {
        toast.info(isEs ? 'Ya estás al día con GitHub' : 'Already up to date with GitHub');
      } else {
        toast.success(
          isEs
            ? `Sincronizado · +${summary.added} mod ${summary.modified} − ${summary.removed}`
            : `Synced · +${summary.added} mod ${summary.modified} − ${summary.removed}`
        );
      }
      window.dispatchEvent(new CustomEvent('damyxia-reload-internal-module', {
        detail: { moduleId },
      }));
    } catch (error) {
      const detailRaw = error?.response?.data?.detail;
      const detail = typeof detailRaw === 'object' ? (detailRaw?.message || 'unknown') : (detailRaw || error?.message || 'unknown');
      if (detail === 'github_not_connected') {
        toast.error(isEs ? 'Conecta tu cuenta de GitHub primero' : 'Connect your GitHub account first');
      } else if (detail === 'module_has_no_github_source') {
        toast.error(isEs ? 'Este módulo no proviene de GitHub' : 'This module is not from GitHub');
      } else {
        toast.error(isEs ? `No se pudo traer desde GitHub: ${detail}` : `Pull failed: ${detail}`);
      }
    } finally {
      setIsPulling(false);
    }
  };

  // 🟥 V4.1 — Toggle auto-sync push tras Save.
  const handleToggleAutoSync = async () => {
    if (!moduleId || isTogglingAutoSync) return;
    setIsTogglingAutoSync(true);
    const next = !autoSync;
    try {
      await axios.post(`${API}/github/auto-sync`, { module_id: moduleId, enabled: next });
      setAutoSync(next);
      toast.success(
        next
          ? (isEs ? 'Auto-sync activado' : 'Auto-sync enabled')
          : (isEs ? 'Auto-sync desactivado' : 'Auto-sync disabled')
      );
    } catch {
      toast.error(isEs ? 'No se pudo cambiar auto-sync' : 'Could not toggle auto-sync');
    } finally {
      setIsTogglingAutoSync(false);
    }
  };

  // 🟥 CUADRO NEGRO V4 — GitHub Push: sube los archivos al repo.
  const handleGithubPush = async () => {
    if (!moduleId) return;
    // Si el módulo tiene origen GitHub usamos owner/repo de ahí.
    // Si no, preguntamos al usuario.
    let targetRepo = githubSource ? `${githubSource.owner}/${githubSource.repo}` : '';
    let branch = githubSource?.branch || 'main';
    if (!githubSource) {
      // eslint-disable-next-line no-alert
      const input = window.prompt(
        isEs
          ? 'Nombre del repo destino (formato owner/repo). Se creará en tu cuenta si no existe.'
          : 'Target repo (owner/repo format). It will be created in your account if missing.',
        ''
      );
      if (!input) return;
      targetRepo = input.trim();
      // eslint-disable-next-line no-alert
      const br = window.prompt(isEs ? 'Branch:' : 'Branch:', 'main');
      branch = (br || 'main').trim();
    }
    // eslint-disable-next-line no-alert
    const commitMsg = window.prompt(
      isEs ? 'Mensaje de commit:' : 'Commit message:',
      isEs ? 'Actualización desde DamyxIA' : 'Update from DamyxIA'
    );
    if (commitMsg === null) return;

    setIsPushing(true);
    try {
      const res = await axios.post(`${API}/github/push`, {
        module_id: moduleId,
        target_repo: targetRepo,
        branch,
        commit_message: commitMsg.trim() || 'Update from DamyxIA',
        create_if_missing: true,
        private: true,
      });
      if (res.data?.success) {
        const repoUrl = res.data.repo_url;
        const shortSha = (res.data.commit_sha || '').slice(0, 7);
        toast.success(
          isEs ? `Push OK · ${shortSha}` : `Push OK · ${shortSha}`,
          {
            duration: 6000,
            action: {
              label: isEs ? 'Ver en GitHub' : 'View on GitHub',
              onClick: () => window.open(repoUrl, '_blank', 'noopener'),
            },
          }
        );
        window.dispatchEvent(new CustomEvent('damyxia-reload-internal-module', {
          detail: { moduleId },
        }));
      }
    } catch (error) {
      const detailRaw = error?.response?.data?.detail;
      const detail = typeof detailRaw === 'object' ? (detailRaw?.message || 'unknown') : (detailRaw || error?.message || 'unknown');
      if (detail === 'github_not_connected') {
        toast.error(isEs ? 'Conecta tu cuenta de GitHub primero' : 'Connect your GitHub account first');
      } else if (detail?.startsWith?.('cannot_create_repo_for_owner')) {
        toast.error(isEs ? 'No puedes crear repos en esa cuenta. Usa tu usuario.' : 'Cannot create repo under that account. Use your own.');
      } else {
        toast.error(isEs ? `Push falló: ${detail}` : `Push failed: ${detail}`);
      }
    } finally {
      setIsPushing(false);
    }
  };

  if (!Array.isArray(projectFiles) || projectFiles.length === 0) {
    return (
      <div className="p-6 text-slate-400 text-sm">
        {isEs ? 'Este proyecto no tiene archivos.' : 'This project has no files.'}
      </div>
    );
  }

  const canEdit = !!moduleId;

  return (
    <div className="h-full w-full flex flex-col bg-slate-950 text-slate-200" data-testid="project-tree-viewer">
      {/* Barra superior del proyecto: nombre + acciones globales (Push/Pull/ZIP). */}
      {canEdit && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-800 bg-slate-900/40">
          <div className="text-xs text-slate-400 truncate flex items-center gap-2">
            <span className="font-medium text-slate-200">{projectName || (isEs ? 'Proyecto' : 'Project')}</span>
            <span className="opacity-70">· {allFiles.length} {isEs ? 'archivos' : 'files'}</span>
            {githubSource && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-violet-500/10 text-violet-300"
                title={`${githubSource.owner}/${githubSource.repo}@${githubSource.branch}`}
              >
                <Github className="w-3 h-3" />
                {githubSource.owner}/{githubSource.repo}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {githubSource && (
              <button
                type="button"
                onClick={handleToggleAutoSync}
                disabled={isTogglingAutoSync}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md transition-colors disabled:opacity-60 ${
                  autoSync
                    ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30'
                    : 'bg-slate-700/40 text-slate-300 hover:bg-slate-700'
                }`}
                data-testid="project-auto-sync-toggle"
                title={isEs ? 'Push automático al guardar archivos' : 'Auto-push on file save'}
              >
                {isTogglingAutoSync ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                <span>{isEs ? 'Auto-sync' : 'Auto-sync'}: {autoSync ? 'ON' : 'OFF'}</span>
              </button>
            )}
            {githubSource && (
              <BranchPicker
                moduleId={moduleId}
                githubSource={githubSource}
                isEs={isEs}
                onBranchChange={() => {
                  // Tras cambio de branch, recargar módulo desde el padre si hay callback.
                  if (typeof onFilesUpdate === 'function') {
                    // Hint: el padre recargará via re-fetch del módulo.
                    onFilesUpdate(null, { reload: true });
                  }
                }}
              />
            )}
            {githubSource && (
              <CiStatusBadge
                moduleId={moduleId}
                branch={githubSource.branch}
                isEs={isEs}
              />
            )}
            {githubSource && (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 transition-colors"
                data-testid="project-history-btn"
                title={isEs ? 'Ver historial de commits + comparar + revertir' : 'View commit history + compare + revert'}
              >
                <History className="w-3.5 h-3.5" />
                <span>{isEs ? 'Historial' : 'History'}</span>
              </button>
            )}
            {githubSource && (
              <button
                type="button"
                onClick={() => setShowPRs(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 transition-colors"
                data-testid="project-prs-btn"
                title={isEs ? 'Pull Requests · Review IA · Merge' : 'Pull Requests · AI Review · Merge'}
              >
                <GitPullRequest className="w-3.5 h-3.5" />
                <span>{isEs ? 'PRs' : 'PRs'}</span>
              </button>
            )}
            {githubSource && (
              <button
                type="button"
                onClick={() => setShowWorkflows(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                data-testid="project-workflows-btn"
                title={isEs ? 'GitHub Actions · workflows · runs · logs' : 'GitHub Actions · workflows · runs · logs'}
              >
                <Workflow className="w-3.5 h-3.5" />
                <span>CI</span>
              </button>
            )}
            {githubSource && (
              <button
                type="button"
                onClick={() => setShowDeploys(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 transition-colors"
                data-testid="project-deploys-btn"
                title={isEs ? 'Deploys · estado · re-deploy · logs' : 'Deploys · status · re-deploy · logs'}
              >
                <Rocket className="w-3.5 h-3.5" />
                <span>{isEs ? 'Deploys' : 'Deploys'}</span>
              </button>
            )}
            {githubSource && (
              <button
                type="button"
                onClick={() => setShowEnvVars(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors"
                data-testid="project-env-btn"
                title={isEs ? 'Variables de entorno · Secrets cifrados' : 'Env vars · Encrypted secrets'}
              >
                <KeyRound className="w-3.5 h-3.5" />
                <span>Env</span>
              </button>
            )}
            {githubSource && (
              <button
                type="button"
                onClick={handleGithubPull}
                disabled={isPulling}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 transition-colors disabled:opacity-60"
                data-testid="project-github-pull-btn"
                title={isEs ? 'Traer cambios desde GitHub (sobreescribe el módulo)' : 'Pull changes from GitHub (overwrites module)'}
              >
                {isPulling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                <span>{isEs ? 'Pull' : 'Pull'}</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleGithubPush}
              disabled={isPushing}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 transition-colors disabled:opacity-60"
              data-testid="project-github-push-btn"
              title={isEs ? 'Subir el proyecto a GitHub (requiere conectar cuenta)' : 'Push project to GitHub (requires connected account)'}
            >
              {isPushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              <span>{isEs ? 'Push' : 'Push'}</span>
            </button>
            <button
              type="button"
              onClick={handleDownloadZip}
              disabled={isDownloading}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors disabled:opacity-60"
              data-testid="project-download-zip-btn"
              title={isEs ? 'Descargar todo el proyecto como ZIP' : 'Download the entire project as ZIP'}
            >
              {isDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              <span>{isEs ? 'Descargar ZIP' : 'Download ZIP'}</span>
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 flex">
        {/* Árbol */}
        <div className="w-56 min-w-[180px] max-w-[280px] shrink-0 border-r border-slate-800 overflow-y-auto py-2">
          <div className="px-3 pb-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            {projectName || (isEs ? 'Proyecto' : 'Project')} · {allFiles.length}
          </div>
          {topLevel.map((node) => (
            <TreeNode
              key={node.path}
              node={node}
              depth={0}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              onSelect={(n) => {
                if (isEditing) {
                  // Cambiar de archivo cancela la edición actual.
                  setIsEditing(false);
                  setDraft('');
                }
                setSelectedPath(n.path);
              }}
              selectedPath={selectedPath}
            />
          ))}
        </div>

        {/* Viewer / editor */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 bg-slate-900/60 gap-2">
            <div className="text-xs text-slate-400 truncate" data-testid="project-selected-path">
              {selectedFile?.path || ''}
            </div>
            {selectedFile && (
              <div className="flex items-center gap-1">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      disabled={isSaving}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-60"
                      data-testid="project-save-file-btn"
                    >
                      {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      <span>{isEs ? 'Guardar' : 'Save'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded text-slate-400 hover:text-slate-200 hover:bg-slate-700/60 transition-colors disabled:opacity-60"
                      data-testid="project-cancel-file-btn"
                    >
                      <X className="w-3 h-3" />
                      <span>{isEs ? 'Cancelar' : 'Cancel'}</span>
                    </button>
                  </>
                ) : (
                  <>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={handleStartEdit}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded text-slate-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                        data-testid="project-edit-file-btn"
                        title={isEs ? 'Editar este archivo' : 'Edit this file'}
                      >
                        <Pencil className="w-3 h-3" />
                        <span>{isEs ? 'Editar' : 'Edit'}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded text-slate-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                      data-testid="project-copy-file-btn"
                      title={isEs ? 'Copiar contenido del archivo' : 'Copy file content'}
                    >
                      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      <span>
                        {copied ? (isEs ? 'Copiado' : 'Copied') : (isEs ? 'Copiar' : 'Copy')}
                        <span className="ml-1 hidden sm:inline opacity-60">{FILE_LANG_HINT(selectedFile.path)}</span>
                      </span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            {isEditing ? (
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                className="w-full h-full p-4 text-xs leading-relaxed font-mono bg-slate-950 text-slate-200 outline-none resize-none border-0 focus:ring-0"
                data-testid="project-edit-textarea"
                placeholder={isEs ? 'Edita el contenido del archivo…' : 'Edit file content…'}
              />
            ) : (
              <pre className="m-0 p-4 text-xs leading-relaxed whitespace-pre font-mono text-slate-200 select-text">
                {selectedFile?.content || ''}
              </pre>
            )}
          </div>
        </div>
      </div>

      {/* 🟥 V4.1 — Modal de resolución de conflictos */}
      {conflicts && conflicts.length > 0 && (
        <ConflictResolutionModal
          moduleId={moduleId}
          conflicts={conflicts}
          onClose={() => setConflicts(null)}
          onResolved={() => setConflicts(null)}
        />
      )}

      {/* 🟥 V5 — Historial de commits */}
      {showHistory && githubSource && (
        <CommitHistoryPanel
          moduleId={moduleId}
          onClose={() => setShowHistory(false)}
        />
      )}

      {/* 🟥 V6 — Pull Requests + Review IA + Merge */}
      {showPRs && githubSource && (
        <PullRequestsPanel
          moduleId={moduleId}
          githubSource={githubSource}
          isEs={isEs}
          onClose={() => setShowPRs(false)}
          onMerged={() => {
            // Tras merge, el backend ya refrescó project_files en el branch base.
            if (typeof onFilesUpdate === 'function') {
              onFilesUpdate(null, { reload: true });
            }
          }}
        />
      )}

      {/* 🟥 V7 — GitHub Actions (workflows + runs + logs IA) */}
      {showWorkflows && githubSource && (
        <WorkflowsPanel
          moduleId={moduleId}
          githubSource={githubSource}
          isEs={isEs}
          currentBranch={githubSource.branch || 'main'}
          onClose={() => setShowWorkflows(false)}
        />
      )}

      {/* 🟥 V7 — Deploys (GitHub Deployments + re-deploy + logs IA) */}
      {showDeploys && githubSource && (
        <DeploysPanel
          moduleId={moduleId}
          githubSource={githubSource}
          isEs={isEs}
          onClose={() => setShowDeploys(false)}
        />
      )}

      {/* 🟥 V7 — Env Vars (GitHub Actions Secrets + Variables) */}
      {showEnvVars && githubSource && (
        <EnvVarsPanel
          moduleId={moduleId}
          githubSource={githubSource}
          isEs={isEs}
          onClose={() => setShowEnvVars(false)}
        />
      )}
    </div>
  );
};

export default ProjectTreeViewer;
